//@flow
import React, {useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import YouTubePlayer from 'react-youtube';

import {BANNER_MEDIA_ASPECT_RATIO, BANNER_LAYOUTS} from '../../../../constants';
import {useBannerOpened} from '../../../../utils';

import {getImageUrl} from '../../../../../../utils/image_provider';

import type {BannerContentPosition} from '../../../../../../entities';

import {
  extractYoutubeVideoId,
  getYoutubePosterUrl,
} from '../../../../../../utils/youtube';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  preview: {
    display: 'flex',
    gap: '1rem',
    backgroundColor: '#fff',
    width: '100%',
    borderRadius: '.5rem',
    position: 'relative',
    overflow: 'hidden',
  },
  imageContainer: {
    width: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerImage: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    objectFit: 'cover',
    zIndex: 1,
    aspectRatio: 1,
    userSelect: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  content: {
    width: '35%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    zIndex: 2,
    gap: '.5rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: 0,
    color: 'inherit',
    lineHeight: '1.1',
  },
  subTitle: {
    fontSize: '.85rem',
    fontWeight: '500',
    margin: 0,
    color: 'inherit',
  },
  button: {
    margin: '0',
    marginTop: '1rem',
    padding: '.75rem 2rem',
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '80px',
    fontSize: '.85rem',
    fontWeight: '600',
    width: 'max-content',
    cursor: 'pointer',
  },
  videoContainer: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  videoWrap: {
    height: '100%',
    position: 'relative',
  },
  videoIframe: {
    width: '100%',
    height: '100%',
  },
  posterVideoBlur: {
    filter: 'blur(24px)',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

type BannerStyle = {
  content?: any,
  container?: any,
  contentMobile?: any,
};

const getBannerStyles = (layout: BannerContentPosition): BannerStyle => {
  if (layout === BANNER_LAYOUTS.CENTER) {
    return {
      content: {
        left: '50%',
        right: 'initial',
        transform: 'translateX(-50%)',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      },
    };
  }

  if (layout === BANNER_LAYOUTS.LEFT) {
    return {
      content: {
        right: '10%',
        left: 'initial',
      },
    };
  }

  if (layout === BANNER_LAYOUTS.RIGHT) {
    return {
      content: {
        left: '10%',
        right: 'initial',
      },
    };
  }

  return {};
};

export default function DesktopPreview() {
  const classes = useStyles();

  const banner = useBannerOpened();

  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef(null);

  if (!banner) return null;

  const {
    backgroundColor,
    subtitle,
    textColor,
    title,
    bannerTextPosition,
  } = banner;

  const bannerStyles = getBannerStyles(bannerTextPosition);
  const videoId = extractYoutubeVideoId(banner.videoUrl || '');

  return (
    <div className={classes.container}>
      <strong>Versión Desktop</strong>

      <div
        className={classes.preview}
        style={{
          backgroundColor,
          aspectRatio: `${
            BANNER_MEDIA_ASPECT_RATIO[banner.bannerShapeType]?.desktop
          }`,
          ...bannerStyles.container,
        }}
        ref={containerRef}
      >
        {banner.videoUrl ? (
          <div className={classes.videoContainer}>
            <img
              src={getYoutubePosterUrl(videoId)}
              className={classes.posterVideoBlur}
              alt="poster blur"
            />
            <YouTubePlayer
              videoId={videoId}
              style={{
                aspectRatio: '16/9',
              }}
              className={classes.videoWrap}
              iframeClassName={classes.videoIframe}
              opts={{
                playerVars: {
                  autoplay: 1,
                  mute: 1,
                  loop: 1,
                  widget_referrer: 'https://www.google.com/',
                  controls: 0,
                },
              }}
            />
          </div>
        ) : (
          <img
            // className={classes.bannerImage}
            src={getImageUrl(banner.imageId)}
            alt="banner"
            ref={imageRef}
          />
        )}

        <div
          className={classes.content}
          style={{
            color: textColor,
            ...bannerStyles.content,
          }}
        >
          {title && <h3 className={classes.title}>{title}</h3>}
          {subtitle && <h4 className={classes.subTitle}>{subtitle}</h4>}
          {banner.buttonText && (
            <button
              className={classes.button}
              style={{
                backgroundColor: banner.buttonBackgroundColor,
                color: banner.buttonTextColor,
              }}
            >
              {banner.buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
